import Backbone from 'backbone'
import _ from 'lodash'
import WidgetModel from 'scoreboard/dashboards/widgets/widget-model'
import conductor from 'scoreboard/components/conductor'
import sections from 'scoreboard/components/sections'
import WidgetControlView from './widget-control-view'
import togglePrefixedClass from 'scoreboard/components/toggle-prefixed-class'
import guard from 'scoreboard/components/unsaved/guard'
import WidgetEmptyState from './widget-empty-state'
import $ from 'jquery'

var WidgetView = Backbone.View.extend({
	className: 'widget',
	resizable: true,

	controlViewCls: WidgetControlView,

	events: {
		'click': '_click'
	},

	nonFetchProperties: [ 'background', 'showDescription', 'showLabel', 'drilldownSection',
		'drilldownId', 'tablePadding', 'fontSize' ],

	initialize: function (opt) {
		this.container = opt.container
		this.readonly = opt.readonly
		this.canvas = opt.canvas

		// Debounce the render function so that multiple consecutive changes
		// to the widget's properties don't cause multiple renders. No "wait"
		// value is passed in, so this will effectively be called at nextTick()
		let render = _.debounce(() => this.render())

		this.listenTo(this.model.properties, 'change', render)
		this.listenTo(this.model.properties, 'change', this._onPropChange.bind(this))
		this.listenTo(this.model.data, 'change', render)
		this.listenTo(this.model.wob, 'change:outlineWidth change:outlineColor', () => {
			this.adjustSize()
			render()
		})
		this.listenTo(this.model.wob, 'change:locked', () => this.disable(this.disabled()))
		this.listenTo(this.model.wob, 'change:thresholdColoring',
			() => render())
		this.listenTo(this.model.wob, 'resize', () => _.result(this, 'resize'))
	},

	render: function () {
		togglePrefixedClass(this.container, 'background', this.model.properties.get('background') || 'none')

		let outlineWidth = this.model.wob.get('outlineWidth')
		togglePrefixedClass(this.container, 'outline-width', outlineWidth || 'none')

		let outlineColor = this.model.wob.get('outlineColor')
		togglePrefixedClass(this.container, 'outline-color',
				outlineWidth ? (outlineColor || WidgetModel.DEFAULT_OUTLINE_COLOR) : null)

		togglePrefixedClass(this.el, 'table-padding', this.model.properties.get('tablePadding'))

		process.nextTick(() => {
			// #11898 Text widgets migrated from SB2 will still have hex colors, so we'll
			// manually set the border-color style attribute so they don't change.
			// Need to do in in nextTick() because the .content div doesn't exist until
			// the widget-view implementation finishes rendering
			this.$('.content').css('border-color',
				(outlineColor && outlineColor.indexOf('#') === 0) ? outlineColor : '')
		})

		let hasDrilldown = !!this.model.drilldown()
		this.$el.toggleClass('has-drilldown', hasDrilldown)

		// A widget is interactive if it has a drilldown or explicitly says its interactive.
		this.$el.toggleClass('is-interactive', hasDrilldown || this.model.isInteractive())

		this.$el.parent().toggleClass('is-clickthrough', this.model.isClickthrough())

		this.$el.toggleClass('is-locked', this.model.wob.get('locked'))

		return this
	},

	allowConfig: function () {
		return !this.model.wob.get('hasTemplate')
	},

	adjustSize: function () {
		this.autoAdjusting = true
		let dim = this.getAdjustedSize()
		this.model.wob.resize(dim.width, dim.height, undefined, undefined, { animate: true })
		this.autoAdjusting = false
	},

	getAdjustedSize: function () {
		let content = this.$('.content').get(0)
		return {
			height: content.clientHeight,
			width: content.clientWidth
		}
	},

	_onPropChange: function () {
		var stale = _(this.model.properties.changedAttributes()).omit(this.nonFetchProperties).keys().value().length > 0

		if (stale) {
			this.model.data.fetch({periodId: this.model.options.getPeriodId()})
		}
	},

	_click: function (e) {
		if (!this.canvas.readonly || !this.$el.hasClass('has-drilldown')) {
			return
		}

		if (this.isDrilldownExcluded(e.target)) {
			return
		}

		// #28922 #45099 clicking on interactive elements should not trigger
		// a drilldown
		let elementsToIgnore = [ 'a', 'button', 'textarea' ]
		if (_.includes(elementsToIgnore, e.target.tagName.toLowerCase())) {
			return
		}

		this.drilldown()
	},

	drilldown: function () {
		if (this.widgetEmptyState) {
			return
		}

		var drilldown = this.model.drilldown()
		if (drilldown && drilldown.section && drilldown.id) {
			guard.conditionallyRun(() => {
				conductor.drilldown(sections.toUrl(drilldown.section) + '/' + drilldown.id, {trigger: true})
			})
		}
	},

	isDrilldownExcluded: function(el) {
		return false
	},

	/**
	 * @param el
	 * @param selector
	 * @return {boolean} true if element equals any element, returned by the
	 * selector, or any descendant of an element returned by the selector.
	 * Used to figure out if a clicked element is interactive or if it should
	 * trigger a drilldown.
	 */
	elIsOrIsDescendantOfSelector: function(el, selector) {
		return !!_(this.$(selector))
			.find(currEl => currEl === el || $.contains(currEl, el))
	},

	/**
	 * If given an error, renders an empty/error state with the error message.
	 * If not error given, will remove any existing error state
	 */
	toggleEmptyState: function (error, emptyStateOptions) {
		let hasError = !!(error || emptyStateOptions)
		this.$el.toggleClass('has-widget-empty-state', hasError)
		if (hasError) {
			let emptyStateContainer = this.$('.empty-state-container')
			emptyStateContainer.css({
				// Fixes #57868
				// Matches parent's height and width, even on resize
				height: `100%`,
				width: `100%`
			})

			if (!this.widgetEmptyState) {
				this.widgetEmptyState = new WidgetEmptyState(_.extend({
					errorResponse: error,
					container: this.$el.closest('.wob'),
					widgetType: this.model.get('type')
				}, emptyStateOptions))

				emptyStateContainer.append(this.widgetEmptyState.el)

				this.canvas.onPaint(() => {
					this.widgetEmptyState.render()
					this.readyForDisplayAndExport()
				})
			}
		} else {
			this.removeEmptyState()
		}
	},

	readyForDisplayAndExport: function () {
		this.readyForDisplay()
		this.readyForExport()
	},

	readyForDisplay: function () {
		this.trigger('ready')
	},

	/**
	 * It is the responsibility of each WOB to call this function when it
	 * is done rendering/resizing and thus ready to be captured in the export.
	 */
	readyForExport: function () {
		// tracked by dashboards-overview.js
		this.canvas.trigger('exportReady', this.model.wob)
	},

	isOverflowing: function(scrollEl) {
		return this.isOverflowingX(scrollEl) || this.isOverflowingY(scrollEl)
	},

	isOverflowingX: function(scrollEl) {
		return this.isOverflowingDimension(scrollEl, 'Width')
	},

	isOverflowingY: function(scrollEl) {
		return this.isOverflowingDimension(scrollEl, 'Height')
	},

	isOverflowingDimension: function (scrollEl, dim) {
		if (_.isNil(scrollEl[0])) {
			return false
		}

		let el = scrollEl[0]

		// Keep track of the previous overflow setting so we can restore it
		// after testing. Note that we aren't using $.css('overflow'), because
		// that returns the computed style (which may be affected by CSS) instead
		// of what's in the actual style attribute.
		let oldOverflow = el.style.overflow

		// Set overflow to 'auto', which will allow scrolling, then compare
		// offsetHeight/Width to scrollHeight/Width
		el.style.overflow = 'auto'
		let overflowing = el['offset' + dim] < el['scroll' + dim]

		// Reset previous overflow setting
		el.style.overflow = oldOverflow

		return overflowing
	},

	/**
	 * Can be implemented by subclasses if there are interactions that need to be disabled
	 * due to the widget being locked in edit mode
	 */
	disable(disable) { },

	/**
	 * Returns whether or not interactions are disabled on the widget
	 */
	disabled() {
		// if we're in edit mode (!this.readonly) and the wob is locked, click events
		// will get through to the NotesView. In that case, we need to disable it
		// because we don't want dialogs opening up in edit mode
		const editingLockedWob = !this.readonly && this.model.wob.get('locked')
		return editingLockedWob
	},

	/**
	 * Returns the UI state of the widget (sorted columns, dataset drilldowns, etc) in
	 * a format that can be later used to reconstruct it.
	 */
	getState: function () {
		return {}
	},

	removeEmptyState: function () {
		this.widgetEmptyState && this.widgetEmptyState.remove()
		delete this.widgetEmptyState
	},

	/**
	 * Returns whether change events to this widget should be ignored for
	 * the purposes of setting FormView to dirty
	 */
	ignoreChangeEvents () {
		return false
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)
		this.removeEmptyState()
	}
})

export default WidgetView

WidgetView.__filename__ = 'dashboards/widgets/widget-view.js'
